<template>
  <div id="CampaignEditOptions" class="Main-Content">
    <ValidationObserver v-slot="{ invalid }" slim>
      <Page
        v-if="loading"
        :title="`${$t('Editing')} ...`"
        :primaryAction="{
          label: $t('Save'),
          disabled: true
        }"
        :secondaryActions="[{
          label: $t('Notes'),
          disabled: true
        }]"
      >
        <LoadingLayout/>
      </Page>
      <Page
        v-if="!loading"
        :title="`${$t('Editing')} '${campaign.name}'`"
        :primaryAction="{
          label: $t('Save'),
          disabled: invalid || disableSave || saving,
          onAction: save
        }"
        :secondaryActions="[{
          label: $t('Notes'),
          onAction: () => { eventHub.$emit('showCampaignNotes', campaign.id) }
        }]"
      >

        <campaign-edit-header :campaign="campaign"></campaign-edit-header>

        <Layout>
          <LayoutSection size="primary">

            <Card :title="$t('A/B Testing')">
              <CardSection>
                <FormLayout>
                  <Checkbox
                    :label="$t('Enable')"
                    v-model.number="abEnabled"
                    :helpText="$t('campaignEdit.options.abEnableHelp')"
                  />
                  <template v-if="abEnabled">
                    <validation-provider :name="$t('A/B Testing Percentage')" immediate rules="nonZero" v-slot="{ errors }">
                      <TextField
                        :label="$t('A/B Testing Percentage')"
                        v-model="campaign.abTest"
                        type="number"
                        suffix="%"
                        :error="errors[0]"
                      />
                    </validation-provider>
                    <Select
                      :label="$t('Allow Widgets In Control Group')"
                      v-model="selectedIds"
                      :help-text="$t('campaignEdit.options.allowWidgetsInControlHelp')"
                      :options="availableWidgets"
                      :placeholder="$t('campaignEdit.segment.Select a widget')"
                      multiple
                      tags
                    />
                  </template>
                  <Button @click="showHistory">{{ $t('Show History') }}</Button>
                </FormLayout>
              </CardSection>
            </Card>

            <Card :title="$t('Additional Options')">
              <CardSection>
                <FormLayout>
                  <Checkbox
                    :label="$t('Low Priority Exit Intent Offers')"
                    v-model="campaign.widgetConfiguration.lowPriorityExitOffers"
                    :help-text="$t('campaignEdit.options.lowPriorityExitOffersHelp')"
                  />
                  <Checkbox
                    :label="$t('Automatic Offer Collection')"
                    v-model="campaign.autoCollect"
                    :help-text="$t('campaignEdit.options.autoCollectHelp')"
                  />
                </FormLayout>
              </CardSection>
            </Card>

            <Card :title="$t('Default ESP Collection List')">
              <CardSection>
                <Select
                  :help-text="$t('campaignEdit.options.espHelp')"
                  :options="formattedEspConfigs"
                  v-model="campaign.espConfigId"
                />
              </CardSection>
            </Card>
            <Card :title="$t('Offer Flyout')">
              <CardSection>
                <Select
                  :options="smartWidgets"
                  v-model="campaign.widgetConfiguration.smartWidgetId"
                  :help-text="$t('campaignEdit.options.offerFlyoutHelp')"
                />
              </CardSection>
            </Card>
            <Card :title="$t('AFN Tracking (Fanplayr Offers)')">
              <CardSection>
                <FormLayout>
                  <Select
                    v-model="afnConfig.afnMethod"
                    :options="[
                      {
                        label: $t('(none)'),
                        value: ''
                      },
                      {
                        value: 'image',
                        label: $t('Image / Pixel')
                      },
                      {
                        value: 'iframe',
                        label: $t('Iframe')
                      },
                      {
                        value: 'script',
                        label: $t('Script Tag')
                      },
                      {
                        value: 'script+iframe',
                        label: $t('Script Tag within an Iframe')
                      }
                    ]"
                    :help-text="$t('campaignEdit.options.afnMethodHelp')"
                  />
                  <Select
                    v-if="afnConfig.afnMethod"
                    :label="$t('Tracking Frequency')"
                    v-model="afnConfig.afnAlwaysTrack"
                    :options="[
                      {
                        label: $t('Once per session'),
                        value: false
                      },
                      {
                        label: $t('Every page view'),
                        value: true
                      }
                    ]"
                    :help-text="$t('campaignEdit.options.afnFrequencyHelp')"
                  />
                  <validation-provider v-if="afnConfig.afnMethod" :name="$t('Tracking Url')" immediate rules="afnUrl" v-slot="{ errors }">
                    <TextField
                      :label="$t('Tracking Url')"
                      v-model="afnConfig.afnUrl"
                      placeholder="//ad.afn.com/ppc/?28559952C354XT"
                      :help-text="$t('campaignEdit.options.afnUrlHelp')"
                      multiline
                      :error="errors[0]"
                    />
                  </validation-provider>
                </FormLayout>
              </CardSection>
            </Card>
            <Card v-if="singleUseCouponsEnabled" :title="$t('campaignEdit.options.Campaign Preferences')">
              <!-- <CardSection>
                <FormLayout>
                  <Checkbox
                    v-model="singleUseCheckbox"
                    :label="$t('campaignEdit.options.enableSingleUseWarning')"
                    :help-text="$t('campaignEdit.options.singleUseCheckboxHelp')"
                  />
                </FormLayout>
              </CardSection> -->
              <CardSection>
                <FormLayout>
                  <FormLayoutGroup>
                    <validation-provider :name="$t('campaignEdit.options.Single-Use Offer Notify Threshold')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        v-model="prefs.singleUseNotifyThreshold"
                        :label="$t('campaignEdit.options.Single-Use Offer Notify Threshold')"
                        type="number"
                        :helpText="$t('campaignEdit.options.singleUseNotifyThresholdHelp')"
                        :error="errors[0] || singleUseNotifyMinError"
                        :min="parseInt(prefs.singleUseDisableThreshold)"
                      />
                    </validation-provider>
                    <validation-provider :name="$t('campaignEdit.options.Single-Use Offer Disable Threshold')" immediate rules="required" v-slot="{ errors }">
                      <TextField
                        v-model="prefs.singleUseDisableThreshold"
                        :label="$t('campaignEdit.options.Single-Use Offer Disable Threshold')"
                        type="number"
                        :helpText="$t('campaignEdit.options.singleUseDisableThresholdHelp')"
                        :error="errors[0] || singleUseDisableMaxError"
                        :max="parseInt(prefs.singleUseNotifyThreshold)"
                      />
                    </validation-provider>
                  </FormLayoutGroup>
                  <EmailListInput
                    :emails.sync="prefs.singleUseNotifyEmails"
                    :label="$t('campaignEdit.options.Notify Emails')"
                  />
                </FormLayout>
                <!-- <pre>{{prefs}}</pre> -->
              </CardSection>
            </Card>
            <Card v-if="campaign.sessionCouponsEnabled" :title="$t('Deputization (Offer Security)')">
              <CardSection>
                <FormLayout>
                  <div v-html="$t('campaignEdit.options.deputizationMessage')"></div>
                  <!-- <p>
                    <em><b>Note:</b> This feature is deprecated in favor of the newer <a href="https://fanplayr.gitbooks.io/integration-custom/content/offer-security/session-offers.html" target="_blank">Session Offers</a> feature. Do not enable this feature if you have implemented the Session Offers variation of offer security.</em>
                  </p>
                  <p>
                    This feature requires <a href="https://fanplayr.gitbooks.io/integration-custom/content/offer-security/deputization.html" target="_blank">additional integration steps</a>. It is designed to prevent unauthorized use of offer codes on your site by replacing the actual code with a randomly generated one (known as a deputy code) that can only be used during the current user session.
                  </p> -->
                  <Checkbox
                    :label="$t('Enable Deputization')"
                    v-model="campaign.sessionCouponsEnabled"
                  />
                </FormLayout>
              </CardSection>
            </Card>
          </LayoutSection>
        </Layout>

        <PageActions
          :primaryAction="{
            label: $t('Save'),
            disabled: invalid || disableSave || saving,
            onAction: save
          }"
        />

        <!-- <pre style="width: 600px; overflow: auto;"> {{ campaign }} </pre> -->

      </Page>
    </ValidationObserver>
  </div>
</template>

<script>

import CampaignEditHeader from '@/components/CampaignEditHeader'
import EmailListInput from '../components/EmailListInput'
import { extend as ValidateExtend } from 'vee-validate/dist/vee-validate.full'

export default {
  name: 'CampaignEditOptions',
  data () {
    return {
      loading: true,
      saving: false,
      campaign: null,
      espConfigs: null,
      widgets: [],
      abEnabled: false,
      selectedIds: [],
      prefs: null,
      historyLoaded: false,
      history: [],
      singleUseCouponsEnabled: false,

      afnConfig : {}
    }
  },
  mounted() {

    ValidateExtend('nonZero', (value) => {
      if (value && value > 0) {
        return true
      }
      return this.$t('validation.notZero')
    })

    ValidateExtend('afnUrl', (value) => {
      if (value && !value.match(/^(\/\/|https?:\/\/)\w+/i)) {
        return this.$t('validation.validTrackingUrl')
      }
      return true
    })

    NProgress.start()
    Promise.all([
      this.api.getCampaign(this.$route.params.id), 
      this.api.getEndpoints(), 
      this.api.getPublishedWidgetsForAccount(), 
      this.api.getCampaignPrefs(this.$route.params.id),
      this.api.getCurrentAccountPrefs()
    ])
      .then((data) => {
        let [ campaign, endpoints, widgets, prefs, accountPrefs ] = data
        // console.log(data)
        this.campaign = campaign
        this.espConfigs = endpoints.filter(e => {
          return e.type === 'ESP'
        }) || []
        this.widgets = widgets
        if (!this.campaign.widgetConfiguration) {
          this.campaign.widgetConfiguration = {}
        }
        if (!this.campaign.espConfigId) {
          this.campaign.espConfigId = 0
        }
        this.prefs = prefs
        this.singleUseCouponsEnabled = accountPrefs.singleUseCoupons
        if (this.prefs.singleUseNotifyEmails) {
          this.prefs.singleUseNotifyEmails = this.prefs.singleUseNotifyEmails.substring(1, this.prefs.singleUseNotifyEmails.length - 1)
        }
        this.afnConfig = initializeAFNConfig(this.campaign)
        this.selectedIds = this.campaign.widgetConfiguration.controlGroupAllowedWidgetIds
        this.abEnabled = this.campaign.abTest > 0
        this.loading = false
      })
      .catch(this.apiCatch)
      .finally(() => {
        NProgress.done()
      })
  },
  computed: {
    disableSave() {
      return false
    },
    singleUseDisableMaxError() {
      if (parseInt(this.prefs.singleUseDisableThreshold) > parseInt(this.prefs.singleUseNotifyThreshold)) {
        return this.$t('campaignEdit.options.singleUseDisableMaxError')
      }
      return ''
    },
    singleUseNotifyMinError() {
      if (parseInt(this.prefs.singleUseDisableThreshold) > parseInt(this.prefs.singleUseNotifyThreshold)) {
        return this.$t('campaignEdit.options.singleUseNotifyMinError')
      }
      return ''
    },
    formattedEspConfigs() {
      const configs = [{
        label: this.$t('(none)'),
        value: 0
      }]
      configs.push(...this.espConfigs.map(ec => {
        return {
          label: this.getEspName(ec),
          value: ec.id
        }
      }))
      return configs
    },
    availableWidgets() {
      return this.widgets.filter((widget) => {
        return (widget.type === 'Default' || widget.type === 'ExitIntent')
      }).map(w => {
        return {
          label: w.name,
          value: w.id
        }
      })
    },
    smartWidgets() {
      const sw = [{
        label: '(disabled)',
        value: -1
      }]
      sw.push(...this.widgets.filter((widget) => {
        return widget.type === 'Smart'
      }).map(w => {
        return {
          label: w.name,
          value: w.id
        }
      }))
      return sw
    }
  },
  methods: {
    getEspName(espConfig) {
      return JSON.parse(espConfig.config).name
    },
    showHistory() {
      this.eventHub.$emit('showCampaignNotes', this.campaign.id, 'ABTESTING')
    },
    save() {
      NProgress.start()
      this.campaign.applyToCartTrackingUrl = reformatTrackingUrl(this.afnConfig)
      this.campaign.widgetConfiguration.controlGroupAllowedWidgetIds = this.selectedIds
      if (!this.abEnabled) this.campaign.abTest = 0
      if (!this.campaign.espConfigId) this.campaign.espConfigId = null
      if (this.prefs.singleUseNotifyEmails) {
        this.prefs.singleUseNotifyEmails = `"${this.prefs.singleUseNotifyEmails}"`
      }
      this.saving = true
      Promise.all([this.api.updateCampaignOptions(this.campaign), this.api.updateCampaignPrefs(this.campaign.id, this.prefs)])
        .then(() => {
          this.$ui.toast({ title: this.$t('Options Saved') })
        })
        .catch(this.apiCatch)
        .finally(() => {
          if (this.prefs.singleUseNotifyEmails) {
            this.prefs.singleUseNotifyEmails = this.prefs.singleUseNotifyEmails.substring(1, this.prefs.singleUseNotifyEmails.length - 1)
          }
          this.saving = false
          NProgress.done()
        })
    },
  },
  components: {
    CampaignEditHeader,
    EmailListInput
  }
}

function initializeAFNConfig(campaign) {
  let config = {
    afnMethod: '',
    afnAlwaysTrack: false,
    afnUrl: null
  }

  if (!campaign.applyToCartTrackingUrl) {
    return config
  }
  if (campaign.applyToCartTrackingUrl.url) {
    config.afnUrl = campaign.applyToCartTrackingUrl.url 
  }
  if (campaign.applyToCartTrackingUrl.image) {
    config.afnMethod = "image"
  } else if (campaign.applyToCartTrackingUrl.iframe) {
    if (campaign.applyToCartTrackingUrl.script) {
      config.afnMethod = 'script+iframe'
    } else {
      config.afnMethod = "iframe"
    }
  } else if (campaign.applyToCartTrackingUrl.script) {
    config.afnMethod = 'script'
  }
  if (campaign.applyToCartTrackingUrl.alwaysTrack) {
    config.afnAlwaysTrack = true
  }
  return config
}

function reformatTrackingUrl(config) {
  let returnConfig = {
    url: config.afnUrl,
    alwaysTrack: !!config.afnAlwaysTrack
  }
  if (config.afnMethod === '') {
    return null
  }
  let modes = config.afnMethod.split('+')
  modes.forEach(mode => {
    returnConfig[mode] = true
  })
  return returnConfig
}

</script>

<style lang="scss">
// #CampaignEditOptions {

// }
</style>
